import type { ThemeColorsDto } from './schemas/theme-styles.schema';

// The colours of the shop's own parts — the product card, a sale price, the three badges and the rating
// stars — are optional in a theme, so the themes written before them still load. One a theme leaves out
// is drawn in the palette colour that already did its job, which is what those themes showed until now.

/** A colour slot of a theme's palette, required or optional. */
export type ThemeColorKey = keyof ThemeColorsDto;

/** The palette colour each optional shop colour falls back to. */
export const SHOP_COLOR_FALLBACKS = {
  card: 'surfaceAlt', sale: 'accent', badgeNew: 'success', badgeHot: 'primary', badgeSoldOut: 'textMuted', rating: 'accent'
} as const satisfies Partial<Record<ThemeColorKey, ThemeColorKey>>;

// Reads one of a theme's colours, an optional shop colour it left out answered by the palette colour beside it.
export function themeColor(colors: ThemeColorsDto, key: ThemeColorKey): string {
  const own = colors[key];
  if (own) return own;
  const fallback = (SHOP_COLOR_FALLBACKS as Partial<Record<ThemeColorKey, ThemeColorKey>>)[key];
  return fallback ? colors[fallback] ?? '' : '';
}

// Lists every colour a theme draws with, the optional shop colours filled in from their fallbacks.
export const allThemeColors = (colors: ThemeColorsDto): Required<ThemeColorsDto> =>
  ({ ...colors, ...Object.fromEntries(Object.keys(SHOP_COLOR_FALLBACKS).map((key) => [key, themeColor(colors, key as ThemeColorKey)])) }) as Required<ThemeColorsDto>;
